// What a machine reported about its patching, set beside the limit that applies to it and the result: the rows of the Checks table on the
// Patching page. Plain functions (no React). The values come from the patch drift result of cma-api (machine.patch, machine.limits,
// machine.findings, machine.okItems); nothing here decides anything, it only lays out what the API already decided.

const list = (v) => (Array.isArray(v) ? v : []);
const present = (v) => v !== null && v !== undefined && v !== '';

const age = (date, days) => {
  if (!present(date)) return null;
  if (days === 0) return `${date} (today)`;
  return days == null ? date : `${date} (${days} days ago)`;
};
const NOT_REPORTED = 'Not reported (older script)';
const within = (n) => `Within ${n} days`;

// Which findings belong to which row (a finding says which fact it is about by its key).
const FINDING_KEYS = {
  os: ['os'], install: ['install', 'history'], security: ['security', 'history'], scan: ['scan'], restart: ['restart'], failures: ['failures'],
  service: ['service'], defer: ['defer', 'updateControl'], pause: ['pause', 'updateControl'], pin: ['pin', 'updateControl'], report: ['report'],
};

export const rowsOf = (machine) => {
  const patch = machine?.patch || {};
  const limits = machine?.limits || {};
  const sources = machine?.limitSources || {};
  const findings = list(machine?.findings);
  const okKeys = new Set(list(machine?.okItems).map((i) => i.key));
  const os = patch.os || {};
  const restart = patch.restart || {};
  const service = patch.updateService || {};
  const failures = patch.failures || null;
  const scan = patch.scan || null;
  const control = patch.updateControl || null;

  const rows = [];
  const add = (key, label, value, sub, limit, limitKey, on) => {
    const found = findings.find((f) => FINDING_KEYS[key].includes(f.key));
    let state = 'none';
    if (found) state = found.gap === 'unknown' ? 'unknown' : 'finding';
    else if (!on) state = 'off';
    else if (okKeys.has(key) || (key === 'service' && service.state)) state = 'ok';       // the API only reports the service when it is off
    rows.push({ key, label, value, sub, limit, source: limitKey ? sources[limitKey] : null, state, gap: found ? found.gap : null, note: found ? (found.note || '') : '' });
  };

  const name = [os.name, os.release].filter(Boolean).join(' ');
  add('os', 'Windows', name ? `${name} (build ${os.build || '?'})` : null,
    [os.editionId, os.installationType].filter(Boolean).join(', ') || null,
    limits.minFeatureBuild > 0 ? `Build ${limits.minFeatureBuild} or newer` : 'No minimum', 'minFeatureBuild', limits.minFeatureBuild > 0);
  add('install', 'Last Windows update', age(patch.lastInstall?.date, patch.daysSinceInstall) || 'None found', patch.lastInstall?.kb || null,
    present(limits.maxDaysSinceInstall) ? within(limits.maxDaysSinceInstall) : null, 'maxDaysSinceInstall', present(limits.maxDaysSinceInstall));
  add('security', 'Last security update', age(patch.lastSecurityUpdate?.date, patch.daysSinceSecurity) || 'None found', patch.lastSecurityUpdate?.kb || null,
    present(limits.maxDaysSinceSecurity) ? within(limits.maxDaysSinceSecurity) : null, 'maxDaysSinceSecurity', present(limits.maxDaysSinceSecurity));
  add('scan', 'Last update scan', scan ? (age(scan.lastSearchSuccess, patch.daysSinceScan) || 'Unknown') : NOT_REPORTED,
    scan && scan.lastInstallSuccess ? `Last install run ${scan.lastInstallSuccess}` : null,
    limits.maxScanAgeDays > 0 ? within(limits.maxScanAgeDays) : 'Not checked', 'maxScanAgeDays', limits.maxScanAgeDays > 0);
  add('restart', 'Restart', restart.pending ? `Pending${restart.pendingSince ? ` since ${restart.pendingSince}` : ''}` : 'None pending',
    restart.lastStart ? `Started ${restart.lastStart}` : null,
    present(limits.maxPendingRestartDays) ? `May wait ${limits.maxPendingRestartDays} days` : null, 'maxPendingRestartDays', present(limits.maxPendingRestartDays));
  add('failures', 'Failed updates', failures && failures.count !== null && failures.count !== undefined ? `${failures.count} in ${failures.days} days` : 'Unknown',
    failures && failures.storeApps ? `${failures.storeApps} Store app updates not counted` : null,
    present(limits.maxFailures) ? `At most ${limits.maxFailures}` : null, 'maxFailures', present(limits.maxFailures));
  add('service', 'Windows Update service', service.state ? `${service.state} (${service.startType || '?'})` : 'Unknown',
    service.noAutoUpdate === true ? 'Automatic updates switched off by policy' : (service.usesUpdateServer ? 'Uses an update server' : null),
    'Not disabled', null, true);
  add('defer', 'Update deferral', control ? `Quality ${control.qualityDeferDays} days, feature ${control.featureDeferDays} days` : NOT_REPORTED, null,
    limits.maxQualityDeferDays > 0 ? `Quality at most ${limits.maxQualityDeferDays} days` : 'Not checked', 'maxQualityDeferDays', limits.maxQualityDeferDays > 0);
  const paused = control ? [
    control.qualityPaused ? `Quality paused${control.qualityPausedUntil ? ` until ${control.qualityPausedUntil}` : ' by policy'}` : null,
    control.featurePaused ? `Feature paused${control.featurePausedUntil ? ` until ${control.featurePausedUntil}` : ' by policy'}` : null,
  ].filter(Boolean) : [];
  add('pause', 'Updates paused', control ? (paused.length ? paused.join('; ') : 'Not paused') : NOT_REPORTED, null,
    limits.forbidPause >= 1 ? 'Not allowed' : 'Not checked', 'forbidPause', limits.forbidPause >= 1);
  add('pin', 'Pinned Windows release', control ? (control.targetRelease || 'None') : NOT_REPORTED, null,
    limits.forbidPinnedRelease >= 1 ? 'Not allowed' : 'Not checked', 'forbidPinnedRelease', limits.forbidPinnedRelease >= 1);
  add('report', 'Last report', machine?.updatedUtc ? `${String(machine.updatedUtc).slice(0, 16).replace('T', ' ')}${patch.daysSinceReport != null ? ` (${patch.daysSinceReport === 0 ? 'today' : `${patch.daysSinceReport} days ago`})` : ''}` : 'Unknown',
    null, limits.maxReportAgeDays > 0 ? within(limits.maxReportAgeDays) : 'Not checked', 'maxReportAgeDays', limits.maxReportAgeDays > 0);
  return rows;
};

// How a row's result is worded. A finding is worded by the page (it knows the gap's own label and colour).
export const STATES = {
  ok: { label: 'OK', tone: 'ok' },
  off: { label: 'Not checked', tone: 'none' },
  unknown: { label: 'Cannot tell', tone: 'none' },
  none: { label: '-', tone: 'none' },
};
